import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  Chip,
  Button,
  Stack,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import { GET_WALLET_STATS } from "../graphql/wallets";
import { GET_WALLET_BALANCE_HISTORY } from "../graphql/wallet-history";
import { GET_TRADES_FOR_SELECTION, CREATE_TRADE } from "../graphql/trades";
import { GET_UPCOMING_EVENTS, CREATE_EVENT } from "../graphql/events";
import { GET_ME } from "../graphql/users";
import { WalletBalanceHistoryComponent } from "./WalletBalanceHistory";
import { WalletActivitiesComponent } from "./WalletActivities";
import { ManualBalanceAdjustment } from "./ManualBalanceAdjustment";
import TradeFormDialog from "./TradeFormDialog";
import {
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  AccountBalanceWallet,
  SwapHoriz,
  Timeline,
  Add,
  Visibility,
  Info as InfoIcon,
} from "@mui/icons-material";
import { AVERAGE_COST_BASIS, CREATE_POSITION } from "../graphql/positions";
import type { AverageCostBasisResult } from "../types/position";

interface DashboardProps {
  walletId: string | null;
}

const getEventTypeColor = (eventType?: string) => {
  switch (eventType) {
    case "ENTRY_SIGNAL":
      return "success";
    case "EXIT_SIGNAL":
      return "error";
    case "EARNINGS_REPORT":
      return "warning";
    case "NEWS_EVENT":
      return "info";
    default:
      return "default";
  }
};

const getImpactIcon = (impact?: string) => {
  switch (impact) {
    case "POSITIVE":
      return <TrendingUpIcon color="success" fontSize="small" />;
    case "NEGATIVE":
      return <TrendingDownIcon color="error" fontSize="small" />;
    case "NEUTRAL":
      return <RemoveIcon color="action" fontSize="small" />;
    default:
      return <WarningIcon color="warning" fontSize="small" />;
  }
};

export function Dashboard({ walletId }: DashboardProps) {
  if (!walletId) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <AccountBalanceWallet
          sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
        />
        <Typography variant="h6" color="text.secondary">
          Select a wallet to view dashboard
        </Typography>
      </Box>
    );
  }

  // Fetch wallet stats
  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
  } = useQuery(GET_WALLET_STATS, {
    variables: { id: walletId },
    skip: !walletId,
  });

  // Fetch recent wallet activity (limit 5)
  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
  } = useQuery(GET_WALLET_BALANCE_HISTORY, {
    variables: { walletId, limit: 5 },
    skip: !walletId,
  });

  // Fetch recent trades (limit 5, filter by walletId if possible)
  const {
    data: tradesData,
    loading: tradesLoading,
    error: tradesError,
    refetch: refetchTrades,
  } = useQuery(GET_TRADES_FOR_SELECTION, {
    skip: !walletId,
  });

  // Fetch upcoming events
  const {
    data: eventsData,
    loading: eventsLoading,
    error: eventsError,
  } = useQuery(GET_UPCOMING_EVENTS, {
    variables: { limit: 5 },
  });

  // Fetch user data for fee settings
  const { data: userData } = useQuery(GET_ME);

  const recentTrades = (tradesData?.trades || [])
    .filter((t: any) => t.walletId === walletId)
    .sort(
      (a: any, b: any) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
    )
    .slice(0, 5);

  const upcomingEvents = eventsData?.upcomingEvents || [];

  const [selectedTrade, setSelectedTrade] = useState<any | null>(null);
  const tradeId = selectedTrade?.id;
  const { data: acbData, loading: acbLoading } = useQuery<{
    averageCostBasis: AverageCostBasisResult;
  }>(AVERAGE_COST_BASIS, { skip: !tradeId, variables: { tradeId } });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manualAdjustOpen, setManualAdjustOpen] = useState(false);
  const [tradeDialogOpen, setTradeDialogOpen] = useState(false);

  const [createTrade] = useMutation(CREATE_TRADE);
  const [createEvent] = useMutation(CREATE_EVENT);
  const [createPosition] = useMutation(CREATE_POSITION);

  if (statsLoading || activityLoading) return <CircularProgress />;
  if (statsError)
    return (
      <Alert severity="error">Error loading stats: {statsError.message}</Alert>
    );
  if (activityError)
    return (
      <Alert severity="error">
        Error loading activity: {activityError.message}
      </Alert>
    );

  const stats = statsData?.walletStats?.stats;
  const wallet = statsData?.walletStats?.wallet;
  const recentActivity = activityData?.walletBalanceHistory || [];

  // Helper for color coding balance changes
  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case "DEPOSIT":
      case "TRADE_EXECUTION":
        return "success";
      case "WITHDRAWAL":
      case "FEE":
        return "error";
      case "POSITION_CLOSE":
        return "warning";
      default:
        return "default";
    }
  };

  const formatEventTime = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Handler for saving a trade
  // Note: The TradeFormDialog provides tradeFee as a percentage (%), not an absolute amount.
  // The backend expects:
  // - fee: absolute fee amount (initially 0, calculated later based on position values)
  // - feePercentage: fee percentage from the form
  // - feeCurrency: user's default fee currency
  //
  // The backend will automatically calculate the actual fee amount when positions are created
  // using the formula: totalPositionValue * (feePercentage / 100)
  const handleTradeSave = async (values: any) => {
    const tradeInput = {
      symbol: values.symbol,
      date: values.date.toISOString(),
      side: values.side,
      notes: values.notes || "",
      walletId: walletId || values.walletId,
      fee: 0, // Initial fee amount (will be calculated by backend based on position values)
      feePercentage: values.tradeFee || 0, // Fee percentage from form (Trade Fee %)
      feeCurrency: userData?.me?.feeCurrency || "USD",
    };
    const result = await createTrade({
      variables: { createTradeInput: tradeInput },
    });
    if (result.data?.createTrade) {
      await refetchTrades();
      return result.data.createTrade;
    }
    return null;
  };

  const handleTradeClick = (trade: any) => {
    setSelectedTrade(trade);
    setDialogOpen(true);
  };

  return (
    <Box>
      {/* Header */}
      {wallet && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <AccountBalanceWallet />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {wallet.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {wallet.currency} • {wallet.status}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Stats Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Card
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            height: "100%",
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Current Balance
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {wallet?.balance ? formatCurrency(wallet.balance) : "-"}
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white",
            height: "100%",
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Total Trades
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {stats?.totalTrades ?? "0"}
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            color: "white",
            height: "100%",
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Open Positions
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {stats?.openPositions ?? "0"}
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
            color: "white",
            height: "100%",
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Total Positions
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {stats?.totalPositions ?? "0"}
            </Typography>
          </CardContent>
        </Card>

        {/* Fee Settings Card */}
        <Card
          sx={{
            background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
            color: "text.primary",
            height: "100%",
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Fee Settings
            </Typography>
            <Box component="div" sx={{ mb: 1 }}>
              <Typography variant="body2" component="span">
                Trade: {userData?.me?.defaultTradeFee ?? 0}% • Position:{" "}
                {userData?.me?.defaultPositionFee ?? 0}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {userData?.me?.feeCurrency || "USD"}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Main Content Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "3fr 2fr",
          },
          gap: 2,
        }}
      >
        {/* Left Column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Recent Trades */}
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Recent Trades
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setTradeDialogOpen(true)}
                >
                  Add Trade
                </Button>
              </Box>
              {tradesLoading ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : tradesError ? (
                <Alert severity="error">
                  Error loading trades: {tradesError.message}
                </Alert>
              ) : recentTrades.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <SwapHoriz
                    sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                  />
                  <Typography color="text.secondary">No trades yet</Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {recentTrades.map((trade: any) => (
                    <ListItem
                      key={trade.id}
                      data-testid={`trade-item-${trade.symbol}`}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="details"
                          onClick={() => handleTradeClick(trade)}
                        >
                          <InfoIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography component="span">
                            {trade.symbol} - {trade.side.toUpperCase()}{" "}
                            {trade.positions?.[0]?.quantity} @ $
                            {trade.positions?.[0]?.price}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            {new Date(trade.date).toLocaleDateString()}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Recent Activity
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setManualAdjustOpen(true)}
                >
                  Manual Adjustment
                </Button>
              </Box>
              <WalletActivitiesComponent walletId={walletId} limit={5} />
            </CardContent>
          </Card>
        </Box>

        {/* Right Column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Upcoming Events */}
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Upcoming Events
              </Typography>
              {eventsLoading ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : eventsError ? (
                <Alert severity="error" sx={{ fontSize: "0.875rem" }}>
                  Error loading events: {eventsError.message}
                </Alert>
              ) : upcomingEvents.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <EventIcon
                    sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No upcoming events
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {upcomingEvents.map((event: any, index: number) => (
                    <React.Fragment key={event.id}>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                              }}
                            >
                              <Chip
                                label={event.eventType || event.type}
                                color={
                                  getEventTypeColor(event.eventType) as any
                                }
                                size="small"
                              />
                              {event.impact && getImpactIcon(event.impact)}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {event.description ||
                                  event.eventType ||
                                  event.type}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatEventTime(event.date)}
                              </Typography>
                              {(event.probability || event.targetPrice) && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {event.probability && (
                                    <Chip
                                      label={`${event.probability}%`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {event.targetPrice && (
                                    <Chip
                                      label={`$${event.targetPrice}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < upcomingEvents.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Recent Balance Changes */}
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Balance Changes
              </Typography>
              {recentActivity.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Timeline
                    sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No recent changes
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {recentActivity.map((history: any, index: number) => (
                    <React.Fragment key={history.id || index}>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                              }}
                            >
                              <Chip
                                label={history.changeType}
                                color={
                                  getChangeTypeColor(history.changeType) as any
                                }
                                size="small"
                              />
                              <Typography
                                variant="body2"
                                color={
                                  history.changeAmount >= 0
                                    ? "success.main"
                                    : "error.main"
                                }
                                fontWeight="bold"
                              >
                                {history.changeAmount >= 0 ? "+" : ""}
                                {formatCurrency(history.changeAmount)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {new Date(history.timestamp).toLocaleString()}
                              </Typography>
                              {history.reason && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  {history.reason}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Dialogs */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Trade Details
            </Typography>
            {selectedTrade && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedTrade.symbol} ({selectedTrade.side.toUpperCase()})
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {new Date(selectedTrade.date).toLocaleString()}
                </Typography>
                {selectedTrade.positions?.[0] && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Quantity:</strong>{" "}
                      {selectedTrade.positions[0].quantity}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Price:</strong> $
                      {selectedTrade.positions[0].price}
                    </Typography>
                    <Box mt={1}>
                      {acbLoading ? (
                        <Typography variant="caption" color="text.secondary">
                          Loading ACB...
                        </Typography>
                      ) : acbData?.averageCostBasis ? (
                        <Box mt={1} data-testid="acb-display">
                          <Typography variant="body2" component="span">
                            Avg Cost: $
                            {acbData.averageCostBasis.averageCostBasis.toFixed(
                              2,
                            )}
                          </Typography>
                          <Typography variant="body2" component="span">
                            Qty: {acbData.averageCostBasis.totalQuantity}
                          </Typography>
                          <Typography variant="body2" component="span">
                            Total: $
                            {acbData.averageCostBasis.totalCost.toFixed(2)}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                )}
                {selectedTrade.notes && (
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    <strong>Notes:</strong> {selectedTrade.notes}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Dialog>

      <ManualBalanceAdjustment
        open={manualAdjustOpen}
        walletId={walletId}
        onClose={() => setManualAdjustOpen(false)}
      />

      <TradeFormDialog
        open={tradeDialogOpen}
        onClose={() => setTradeDialogOpen(false)}
        onSave={handleTradeSave}
        onEventCreate={createEvent}
        onEventUpdate={async () => {}}
        onEventDelete={async () => {}}
        onPositionCreate={createPosition}
        onPositionUpdate={async () => {}}
        onPositionDelete={async () => {}}
        onRefresh={refetchTrades}
      />
    </Box>
  );
}
